// AI Presence audit rows (API: lib/controllers/aiPresence projectAudit) and
// the report shapes lib/aiPresence/report.js stores (report.version 1).
// Every answer comes from the vendor API with web search on — a benchmark
// proxy for the consumer apps, labeled as such in the report.

export type AiPresenceStatus = 'pending' | 'ready' | 'error';
export type AiPresenceKind = 'product' | 'brand';
export type AipPlatform = 'claude' | 'gemini' | 'openai';
export type AipIntent = 'discovery' | 'use_case' | 'comparison' | 'criteria' | 'branded';
export type AipRecommendation = 'explicit' | 'qualified' | 'none';

export const AIP_PLATFORM_LABELS: Record<string, string> = {
  claude: 'Claude',
  gemini: 'Google Gemini',
  openai: 'OpenAI',
};

export const AIP_INTENT_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  use_case: 'Use case',
  comparison: 'Comparison',
  criteria: 'Purchase criteria',
  branded: 'Branded research',
};

export interface AipPlatformScore {
  score: number | null;
  discovery: number | null;
  recommendation: number | null;
  eligible?: number;
  errors?: number;
  mentions?: number;
  explicit?: number;
  qualified?: number;
  models?: string[];
  surface?: string;
  products_scored?: number;
  label?: string;
}

export interface AipPromptRollupPlatform {
  samples: number;
  judged: number;
  errors: number;
  mentioned: number;
  mention_rate: number | null;
  avg_credit: number | null;
  explicit: number;
  qualified: number;
  best_rank: number | null;
  top_competitors: string[];
  summary: string | null;
  sentiment: string | null;
}

export interface AipPromptRollup {
  key: string;
  text: string;
  intent: AipIntent;
  intent_label: string;
  branded: boolean;
  keyword: string | null;
  est_monthly_searches: number | null;
  source: string | null;
  mention_rate: number | null;
  avg_credit: number | null;
  platforms: Record<string, AipPromptRollupPlatform>;
}

export interface AipCompetitorRow {
  name: string;
  brand: string | null;
  appearance_rate: number | null;
  explicit_rate: number | null;
  appearances: number;
  platforms: Record<string, number | null>;
}

export interface AipCompetitors {
  eligible: number;
  us: { name: string; appearance_rate: number | null; explicit_rate: number | null; platforms: Record<string, number | null> };
  competitors: AipCompetitorRow[];
  siblings?: AipCompetitorRow[];
}

export interface AipDomain {
  domain: string;
  answers: number;
  cited?: number;
  with_us: number;
  without_us: number;
  own: 'own_pdp' | 'own_site' | null;
  platforms?: Record<string, number>;
}

export interface AipUrl {
  url: string;
  title: string | null;
  domain: string;
  count: number;
  cited: number;
  own: 'own_pdp' | 'own_site' | null;
}

export interface AipSources {
  answers_with_sources?: number;
  own_pdp_cited_answers?: number;
  own_pdp_cited_rate?: number | null;
  domains: AipDomain[];
  urls?: AipUrl[];
  own_urls?: AipUrl[];
}

export interface AipInaccurateClaim {
  claim: string;
  note: string | null;
  count: number;
  platforms: string[];
  prompts: string[];
  product?: string;
}

export interface AipOpportunity {
  key: string;
  type: 'prompt_gap' | 'weak_recommendation' | 'source_gap' | 'pdp_not_cited' | 'inaccurate_claim' | 'expert_content';
  title: string;
  detail: string;
  action: string;
  priority: number;
  evidence?: Record<string, any>;
  product?: string;
  audit_id?: number;
}

export const AIP_OPPORTUNITY_TYPE_LABELS: Record<string, string> = {
  prompt_gap: 'Prompt gap',
  weak_recommendation: 'Weak recommendation',
  source_gap: 'Source gap',
  pdp_not_cited: 'PDP not cited',
  inaccurate_claim: 'Inaccurate claim',
  expert_content: 'Expert content',
};

export interface AipTrend {
  previous_audit_id: number;
  previous_scored_at: string;
  overall: { previous: number | null; delta: number | null };
  discovery: { previous: number | null; delta: number | null };
  recommendation: { previous: number | null; delta: number | null };
  platforms: Record<string, { previous: number | null; delta: number | null }>;
  prompt_mention_rate: Record<string, number | null>;
}

export interface AipBrandedFinding {
  key: string;
  text: string;
  platforms: Record<string, { judged: number; summary: string | null; sentiment: string | null; recommended: number }>;
}

export interface AipReportMeta {
  surface: string;
  surface_label: string;
  samples: number;
  platforms: string[];
  prompt_count: number;
  unbranded_count: number;
  branded_count: number;
  prompt_set: { id: number | null; version: number | null; ephemeral?: boolean; created_at?: string | null };
  competitors_tracked: string[];
  // Shared category answers (asked once for the brand's category).
  category?: { name: string; audit_id: number; prompt_set_id: number; version: number; answered_at: string | null } | null;
  inputs: Record<string, any> | null;
  scoring_version: number;
  usage?: Record<string, any>;
}

export interface AiPresenceProductReport {
  version: number;
  kind: 'product';
  label: string;
  subject: {
    brand_name: string;
    product_name: string;
    category?: string;
    url?: string;
    pdp_urls?: Array<{ url: string; hostname: string; brand_site: boolean }>;
  };
  overall: { score: number | null; discovery: number | null; recommendation: number | null };
  platforms: Record<string, AipPlatformScore & { label: string }>;
  prompts: AipPromptRollup[];
  competitors: AipCompetitors;
  sources: AipSources;
  claims: { counts: { accurate: number; inaccurate: number; unverifiable: number }; inaccurate: AipInaccurateClaim[] };
  branded: AipBrandedFinding[];
  opportunities: AipOpportunity[];
  trend: AipTrend | null;
  fact_sheet: { summary: string; facts: string[]; source_url: string | null } | null;
  meta: AipReportMeta;
}

export interface AiPresenceBrandReport {
  version: number;
  kind: 'brand';
  label: string;
  overall: { score: number | null; discovery: number | null; recommendation: number | null };
  platforms: Record<string, AipPlatformScore & { label: string }>;
  per_product: Array<{
    audit_id: number;
    desireid: number;
    title: string;
    status: AiPresenceStatus;
    overall_score: number | null;
    discovery_score: number | null;
    recommendation_score: number | null;
    platform_scores: Record<string, AipPlatformScore> | null;
    top_competitor: AipCompetitorRow | null;
  }>;
  competitors: Array<{ name: string; brand: string | null; products: number; avg_appearance_rate: number }>;
  sources: { domains: AipDomain[] };
  inaccurate_claims: AipInaccurateClaim[];
  top_opportunities: AipOpportunity[];
  products_analyzed: number;
  products_scored: number;
  meta: { surface_label: string; platforms: string[] };
}

export interface AiPresenceAudit {
  id: number;
  kind: AiPresenceKind;
  desireid: number | null;
  subject_brand_id: number | null;
  brand_id: number | null;
  parent_id: number | null;
  prompt_set_id: number | null;
  url: string | null;
  status: AiPresenceStatus;
  generation_error: string | null;
  overall_score: number | null;
  discovery_score: number | null;
  recommendation_score: number | null;
  platform_scores: Record<string, AipPlatformScore> | null;
  params: {
    brand_name: string | null;
    product_name: string | null;
    size: 'benchmark' | 'quick' | null;
    platforms: string[] | null;
    product_count: number | null;
    progress: { done: number; total: number } | null;
    product_audit_ids: number[] | null;
  };
  started_at: string | null;
  scored_at: string | null;
  created_at: string;
  updated_at: string;
  report?: AiPresenceProductReport | AiPresenceBrandReport | null;
}

export interface AipResponse {
  id: number;
  prompt_key: string;
  platform: AipPlatform;
  model: string | null;
  surface: string | null;
  sample: number;
  status: 'answered' | 'judged' | 'error';
  error: string | null;
  response_text: string | null;
  sources: Array<{ url: string; title: string | null; cited: boolean; own?: string | null }>;
  mentioned: boolean | null;
  mention_target: 'product' | 'brand' | 'none' | null;
  recommendation: AipRecommendation | null;
  credit: number | null;
  rank: number | null;
  competitors: Array<{ name: string; brand: string | null; recommendation: AipRecommendation }>;
  claims: Array<{ claim: string; verdict: 'accurate' | 'inaccurate' | 'unverifiable'; note: string }>;
  judge: { summary: string; sentiment: string } | null;
}

export interface AipSubscores {
  discovery: number | null;
  recommendation: number | null;
  platforms?: Record<string, AipPlatformScore> | null;
}

export const AIP_PENDING_STALE_MS = 60 * 60 * 1000;

export const isAipRunning = (a: Pick<AiPresenceAudit, 'status' | 'updated_at'> | null | undefined) =>
  !!a && a.status === 'pending' && Date.now() - new Date(a.updated_at).getTime() < AIP_PENDING_STALE_MS;

export const aipSubjectLabel = (a: AiPresenceAudit) =>
  a.kind === 'brand'
    ? a.params.brand_name || `Brand ${a.subject_brand_id}`
    : a.params.product_name || a.url || `Product ${a.desireid}`;

export const isAipProductReport = (r: AiPresenceAudit['report']): r is AiPresenceProductReport => !!r && r.kind === 'product';
